import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { SiteProvider } from "@/context/SiteContext";
import { AuthProvider } from "@/context/AuthContext";
import { BASE_PATH } from "@/lib/api";

import PublicLayout from "@/components/public/PublicLayout";
import Home from "@/pages/public/Home";
import ContentPage from "@/pages/public/ContentPage";
import { ProgramDetail, ProgramsIndex } from "@/pages/public/Programs";
import { BlogIndex, BlogPost } from "@/pages/public/Blog";
import Testimonials from "@/pages/public/Testimonials";
import { Contact, GetInvolved, GetSupport } from "@/pages/public/Forms";
import Donate from "@/pages/public/Donate";
import Team from "@/pages/public/Team";
import EmeraldHonours from "@/pages/public/EmeraldHonours";
import EmeraldNominate from "@/pages/public/EmeraldNominate";
import { GalleryCategory, GalleryIndex } from "@/pages/public/Gallery";
import NotFound from "@/pages/public/NotFound";

import AdminLayout, { RequireCapability } from "@/components/admin/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import { ContentEditor, ContentList } from "@/pages/admin/Content";
import Nominations from "@/pages/admin/Nominations";
import { AdminEnquiries, AdminTestimonials } from "@/pages/admin/Moderation";
import MediaLibrary from "@/pages/admin/MediaLibrary";
import { EmeraldMedia, EmeraldSettings } from "@/pages/admin/Emerald";
import { EmeraldGallery } from "@/pages/admin/EmeraldGallery";
import { Homepage } from "@/pages/admin/Homepage";
import { Videos } from "@/pages/admin/Videos";
import { Galleries } from "@/pages/admin/Galleries";
import { AuditLog, DonationSettings, SiteSettings, Users } from "@/pages/admin/SettingsPages";

export default function App() {
  return (
    <BrowserRouter basename={BASE_PATH || undefined}>
      <AuthProvider>
        <SiteProvider>
          <Routes>
            {/* ---------------------------------------------- public site */}
            <Route element={<PublicLayout />}>
              <Route index element={<Home />} />
              <Route path="/about" element={<ContentPage slug="about" />} />
              <Route path="/team" element={<Team />} />
              <Route path="/kidney-health" element={<ContentPage slug="kidney-health" />} />
              <Route path="/our-efforts" element={<ProgramsIndex />} />
              <Route path="/programs/:slug" element={<ProgramDetail />} />
              <Route path="/medication-assistance" element={<ContentPage slug="medication-assistance" />} />
              <Route path="/research" element={<ContentPage slug="research" />} />
              <Route path="/get-support" element={<GetSupport />} />
              <Route path="/get-involved" element={<GetInvolved />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/blog" element={<BlogIndex />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/emerald-honours" element={<EmeraldHonours />} />
              <Route path="/emerald-honours/nominate" element={<EmeraldNominate />} />
              <Route path="/donate" element={<Donate />} />
              <Route path="/gallery" element={<GalleryIndex />} />
              <Route path="/gallery/:slug" element={<GalleryCategory />} />

              {/* legacy route redirects (one canonical URL per page) */}
              <Route path="/about-us" element={<Navigate to="/about" replace />} />
              <Route path="/get-help" element={<Navigate to="/get-support" replace />} />
              <Route path="/programs" element={<Navigate to="/our-efforts" replace />} />
              <Route path="/nominate" element={<Navigate to="/emerald-honours/nominate" replace />} />
              <Route path="/emerald" element={<Navigate to="/emerald-honours" replace />} />
              <Route path="/news" element={<Navigate to="/blog" replace />} />
              <Route path="/contact-us" element={<Navigate to="/contact" replace />} />

              <Route path="*" element={<NotFound />} />
            </Route>

            {/* ------------------------------------------------- CMS */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />

              <Route path="pages" element={<ContentList kind="pages" />} />
              <Route path="pages/:id" element={<ContentEditor kind="pages" />} />
              <Route path="kidney-health" element={<Navigate to="/admin/pages" replace />} />

              <Route path="posts" element={<ContentList kind="posts" />} />
              <Route path="posts/:id" element={<ContentEditor kind="posts" />} />

              <Route path="programs" element={<ContentList kind="programs" />} />
              <Route path="programs/:id" element={<ContentEditor kind="programs" />} />

              <Route
                path="testimonials"
                element={
                  <RequireCapability capability="moderate">
                    <AdminTestimonials />
                  </RequireCapability>
                }
              />
              <Route
                path="enquiries"
                element={
                  <RequireCapability capability="moderate">
                    <AdminEnquiries />
                  </RequireCapability>
                }
              />
              <Route
                path="emerald/nominations"
                element={
                  <RequireCapability capability="moderate">
                    <Nominations />
                  </RequireCapability>
                }
              />
              <Route path="emerald/gallery" element={<EmeraldGallery />} />
              <Route path="homepage" element={<Homepage />} />
              <Route path="videos" element={<Videos />} />
              <Route path="emerald/media" element={<EmeraldMedia />} />
              <Route
                path="emerald/settings"
                element={
                  <RequireCapability capability="settings.write">
                    <EmeraldSettings />
                  </RequireCapability>
                }
              />
              <Route path="galleries" element={<Galleries />} />
              <Route path="media" element={<MediaLibrary />} />
              <Route
                path="donations"
                element={
                  <RequireCapability capability="settings.write">
                    <DonationSettings />
                  </RequireCapability>
                }
              />
              <Route
                path="users"
                element={
                  <RequireCapability capability="users.manage">
                    <Users />
                  </RequireCapability>
                }
              />
              <Route
                path="settings"
                element={
                  <RequireCapability capability="settings.write">
                    <SiteSettings />
                  </RequireCapability>
                }
              />
              <Route
                path="audit-log"
                element={
                  <RequireCapability capability="audit.read">
                    <AuditLog />
                  </RequireCapability>
                }
              />
            </Route>
          </Routes>
        </SiteProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
